import styled, { css, keyframes } from 'styled-components';

import { Button } from '@/nova/components/Button/Button';
import { Skeleton } from '@/nova/components/Skeleton/Skeleton';
import { Text } from '@/nova/components/Text/Text';
import {
  AFFILIATE_FORM,
  ANIM,
  COLORS,
  LIST_WIZARD,
  LIST_WIZARD_ANIM,
  MOBILE,
  REDUCED_MOTION,
  SPACE,
  STATUS_COLORS,
} from '@/nova/theme/tokens';
import { ADDED_FLASH_MS, addedFlash } from '../../creatives.styles';

/* The custom-list editor, a wizard of three steps in one sheet: the way the list is built (Method), its games
   (Games: one filter bar for both ways, then an automatic list's games across the whole width, or a hand-picked
   list's results on the left and its games on the right, as the concept's "B2" lays them out), then its name and
   layout. Colours and timings are tokens, spacing is the SPACE scale. */

/* The sheet's header: the title, and the steps beside it (under it, once they no longer fit, as on a phone). */
export const WizardHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]} ${SPACE[24]};
  min-width: 0;
  flex-wrap: wrap;
`;

/* The whole editor, whichever step it is on. Its HEIGHT is measured and set by the component (the room the sheet
   leaves it), so the panes scroll their own lists and the sheet's scroll area never has to scroll too. It arrives
   as a custom property so the phone rule below can still override it; an inline height could not be overridden
   at all. */
export const Editor = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  min-height: 0;
  height: var(--editor-h, 760px);

  ${MOBILE} {
    height: auto;
  }
`;

/* ----------------------------------------------------------- steps 1 and 3: a form in the middle */

/* Method and Name and layout fill the editor's measured height and hold their form in the middle, both ways: the
   question over its two cards, or the one column of the name and the layout. At least that tall, and never shrunk
   to it: a form taller than the room (a short window, a long language) makes the sheet scroll, where a pane held to
   the room centred it and spilled its top out above the editor, out of reach (as the editor's flex item, the pane
   shrank to a bare `min-height` just as to a `height`). On a phone the editor's height is auto, so the percentage is
   nothing and the pane is as tall as its form: no empty space is added around it. */
export const StepPane = styled.div`
  flex: none;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[32]};
`;

/* ----------------------------------------------------------- step 3: name and layout */

/* The form as the design draws it: one column at most 600px wide, the name block over the layout block. On a phone the
   two blocks keep the 16px between them they have always had. */
export const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[44]};
  width: 100%;
  max-width: 600px;

  ${MOBILE} {
    gap: ${SPACE[16]};
  }
`;

/* The name block and the layout block: a heading, then what it asks for. */
export const StepBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

/* Set as the panes' titles are. A plain heading holding its Text, so the step's first one can take the keyboard
   (Back and the stepper leave it there). */
export const StepTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

const nameNudge = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 transparent;
  }
  30% {
    box-shadow: 0 0 0 3px ${COLORS.ctaSolid};
  }
`;

/** How long a flash lasts; the component clears the mark on the same beat. */
export const FLASH_MS = ADDED_FLASH_MS;

/** Wraps the name field so it can flash when the confirm button sends the owner back to it. */
export const NameWrap = styled.div<{ $flash: boolean }>`
  border-radius: 12px;
  animation: ${({ $flash }) => ($flash ? nameNudge : 'none')} ${FLASH_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* Under the name field: where an automatic list's suggested name comes from, and the action that goes with it (Reset
   to suggested, Use suggested name). As tall as that action, so the layout below does not move when it appears. */
export const NameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[12]};
  min-height: 30px;
`;

export const NameHint = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/* ----------------------------------------------------------- step 1: the method */

/* A plain heading holding its Text, so it can carry the id that names the group, and take the keyboard. */
export const ModeHeading = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  color: ${COLORS.heading};
`;

/* The radio group of the two ways, named by the question above it. */
export const ModeSet = styled.fieldset<{ $three?: boolean }>`
  width: 100%;
  max-width: ${({ $three }) => ($three ? '1120px' : '780px')};
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

/* The two cards side by side, each as tall as its own content; three, when a list may also start from the market. */
export const ModeCards = styled.div<{ $three?: boolean }>`
  display: grid;
  grid-template-columns: repeat(${({ $three }) => ($three ? 3 : 2)}, minmax(0, 1fr));
  align-items: start;
  gap: ${SPACE[20]};
  width: 100%;
  max-width: ${({ $three }) => ($three ? '1120px' : '780px')};

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/* One way of building the list: a real radio, visually hidden, in a label that is the whole card. A grid: the
   icon tile and the badge share the top row, the words take the row under it. */
export const ModeCard = styled.label<{ $on: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: ${SPACE[12]};
  padding: ${SPACE[20]};
  border-radius: 16px;
  text-align: left;
  cursor: pointer;
  background: ${({ $on }) => ($on ? COLORS.ctaTint : COLORS.surfaceSoft)};
  border: 2px solid ${({ $on }) => ($on ? COLORS.ctaBorder : COLORS.panelBorder)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $on }) => ($on ? COLORS.ctaBorder : COLORS.hoverBorderLit)};
  }

  /* The ring while the radio has the keyboard's focus, the radio itself being out of sight. */
  &:has(input:focus-visible) {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }
`;

/* Out of sight but still the control, for assistive tech and the keyboard: the card shows the choice. */
export const ModeRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

/* The method's icon tile: filled while its card is the one picked, neutral otherwise. */
export const ModeIcon = styled.span<{ $on: boolean }>`
  grid-row: 1;
  grid-column: 1;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $on }) => ($on ? COLORS.ctaSolid : COLORS.surfaceMuted)};
  color: ${({ $on }) => ($on ? COLORS.ctaSolidText : COLORS.text)};
`;

export const ModeText = styled.span`
  grid-row: 2;
  grid-column: 1 / -1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/* The name, description and badge are plain spans holding their Text, so they can carry the ids that name the
   card's radio (its name alone) and describe it (the rest). */
export const ModeName = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

/* At the top right of its card, beside the icon tile. */
export const ModeBadge = styled.span`
  grid-row: 1;
  grid-column: 2;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${STATUS_COLORS.positive};
  color: ${STATUS_COLORS.positive};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const ModeHint = styled.span`
  font-size: 15px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/* What the way of building gives, a line each, ticked in the accent on the card picked. */
export const ModeLines = styled.ul<{ $on: boolean }>`
  margin: ${SPACE[4]} 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  font-size: 14px;
  color: ${COLORS.textMuted};

  svg {
    flex: none;
    color: ${({ $on }) => ($on ? COLORS.ctaBorder : COLORS.textMuted)};
  }
`;

export const ModeLine = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

/* ----------------------------------------------------------- the filter bar */

/* Holds the filter bar. On a phone it sticks to the top of the sheet as the sheet scrolls, on the sheet's own
   surface so nothing shows through it; the negative margin takes back its padding at rest. It is held 4px ABOVE
   the scroll area's top, with 4px more padding over the bar, so its backing reaches past the top edge: started at
   the edge itself, a 2-3px seam over the bar let the games show through in Chrome's device mode. The bar sits
   where it did, 8px under the top while held and unmoved at rest. */
export const FilterDock = styled.div`
  ${MOBILE} {
    position: sticky;
    top: -${SPACE[4]};
    z-index: 4;
    margin: -${SPACE[12]} 0 -${SPACE[8]};
    padding: ${SPACE[12]} 0 ${SPACE[8]};
    background: ${AFFILIATE_FORM.surface};
  }
`;

/* One bar for both ways of building: the three facet buttons, then every pick as a chip. Positioned, because
   a facet's panel hangs from it over what is below. */
export const FilterBar = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]} ${SPACE[12]};
  padding: ${SPACE[12]} ${SPACE[16]};
  border-radius: 14px;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${COLORS.hairline};
`;

export const Facets = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};

  /* On a phone the three share the bar's width, so none runs off its edge. */
  ${MOBILE} {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

export const FacetBtn = styled.button<{ $on: boolean }>`
  height: 36px;
  padding: 0 ${SPACE[12]};
  border-radius: 10px;
  border: 1px solid ${({ $on }) => ($on ? COLORS.accent : COLORS.hairline)};
  background: ${({ $on }) => ($on ? COLORS.hoverWash : COLORS.surfaceCard)};
  color: ${COLORS.text};
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }

  ${MOBILE} {
    flex: 1 1 0;
    min-width: 0;
    justify-content: center;
    padding: 0 ${SPACE[8]};
  }
`;

export const FacetCount = styled.span`
  flex: none;
  min-width: 18px;
  padding: 0 ${SPACE[4]};
  border-radius: 999px;
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
`;

export const Chips = styled.div`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
  padding-left: ${SPACE[12]};
  border-left: 1px solid ${COLORS.hairline};

  ${MOBILE} {
    padding-left: 0;
    border-left: 0;
  }
`;

export const FilterChip = styled.button`
  height: 30px;
  padding: 0 ${SPACE[8]} 0 ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  color: ${COLORS.text};
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const ChipLabel = styled(Text).attrs({ tag: 'span' as const })`
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* A quiet action in words: Clear all, Clear, Remove all (the last in the negative colour). */
export const TextAction = styled.button<{ $danger?: boolean }>`
  height: 30px;
  padding: 0 ${SPACE[8]};
  border: 0;
  background: transparent;
  color: ${({ $danger }) => ($danger ? STATUS_COLORS.negative : COLORS.ctaSolid)};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* A small pill button, outlined, or solid for the facet panel's confirm. */
export const SmallBtn = styled.button<{ $solid?: boolean }>`
  flex: none;
  height: 34px;
  padding: 0 ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${({ $solid }) => ($solid ? COLORS.ctaBorder : COLORS.hairline)};
  background: ${({ $solid }) => ($solid ? COLORS.ctaSolid : 'transparent')};
  color: ${({ $solid }) => ($solid ? COLORS.ctaSolidText : COLORS.text)};
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $solid }) => ($solid ? COLORS.ctaBorder : COLORS.hoverBorderLit)};
  }
`;

/* A facet's panel: hangs under the bar, from its button, over the list below. */
export const Menu = styled.div`
  position: absolute;
  top: calc(100% + ${SPACE[8]});
  z-index: 5;
  width: 340px;
  max-width: calc(100vw - ${SPACE[32]});
  display: flex;
  flex-direction: column;
  border: 1px solid ${COLORS.hairline};
  border-radius: 14px;
  background: ${COLORS.surfaceCard};
  box-shadow: ${COLORS.menuShadow};
  overflow: hidden;

  ${MOBILE} {
    left: 0;
    right: 0;
    width: auto;
  }
`;

export const MenuHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACE[12]};
  padding: ${SPACE[12]} ${SPACE[12]} ${SPACE[8]};
`;

export const MenuTitle = styled(Text).attrs({ tag: 'h4' as const })`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const MenuHint = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/* Sticky so it stays reachable while a long list (81 themes) scrolls under it. */
export const MenuSearch = styled.input`
  position: sticky;
  top: 0;
  z-index: 1;
  flex: none;
  width: 100%;
  height: 32px;
  box-sizing: border-box;
  margin-bottom: ${SPACE[4]};
  padding: 0 ${SPACE[12]};
  border-radius: 8px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: ${COLORS.accent};
  }

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  padding: ${SPACE[4]};

  /* On a phone, short enough to open whole under the bar, with the keyboard up for its search. */
  ${MOBILE} {
    max-height: min(320px, 40dvh);
  }
`;

/* A type's row: its tick, and the control that opens its subtypes. */
export const OptRow = styled.div`
  display: flex;
  align-items: center;
`;

export const Opt = styled.button<{ $sub?: boolean; $fill?: boolean }>`
  flex: ${({ $fill }) => ($fill ? '1 1 auto' : 'none')};
  min-width: 0;
  height: ${({ $sub }) => ($sub ? 36 : 40)}px;
  padding: 0 ${SPACE[8]};
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.text};
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  text-align: left;
  font-family: inherit;
  font-size: ${({ $sub }) => ($sub ? 12 : 13)}px;
  font-weight: ${({ $sub }) => ($sub ? 400 : 500)};
  cursor: pointer;

  &:hover {
    background: ${COLORS.hoverWash};
  }
`;

export const Box = styled.span<{ $state: 'on' | 'partial' | 'off' }>`
  width: 16px;
  height: 16px;
  flex: none;
  border-radius: 5px;
  border: 1px solid ${({ $state }) => ($state === 'off' ? COLORS.hairline : COLORS.ctaBorder)};
  background: ${({ $state }) => ($state === 'off' ? 'transparent' : COLORS.ctaSolid)};
  color: ${COLORS.ctaSolidText};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const OptLabel = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const OptCount = styled.span`
  flex: none;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/* An option standing in while the catalogue loads: an option's height and padding, with its tick, label and count. */
export const OptSkeleton = styled.div`
  flex: none;
  height: 40px;
  padding: 0 ${SPACE[8]};
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
`;

export const OptSkeletonCount = styled(Skeleton)`
  margin-left: auto;
`;

export const ExpandBtn = styled.button<{ $open: boolean }>`
  width: 32px;
  height: 32px;
  flex: none;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: ${COLORS.hoverWash};
  }

  svg {
    transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
    transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
`;

export const SubList = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${SPACE[16]};
  padding-left: ${SPACE[8]};
  border-left: 1px solid ${COLORS.hairline};
`;

export const MenuFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]} ${SPACE[12]};
  border-top: 1px solid ${COLORS.hairline};
`;

/* ----------------------------------------------------------- the body */

/* The results a third of the row and the list two thirds, exactly (a grid, as flex would count the results'
   padding and border on top of their share). */
export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: ${SPACE[24]};

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${SPACE[16]};
  }
`;

/* ----------------------------------------------------------- the phone accordion (a hand-picked list) */

/* The results and the list, one open at a time, as tall as the sheet's scroll area (measured, see the
   component): it fills the screen, and the open segment's list scrolls inside it from the first swipe, as nothing
   sits above it on the Games step for the sheet to scroll away first. A screen tall until it is measured, which
   takes the sheet to its cap, the only height it is measured at. */
export const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  height: var(--split-h, 100dvh);
  min-height: 0;
`;

/* The open segment takes all the height its closed sibling's header leaves. */
export const Segment = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${({ $open }) => ($open ? '1 1 0' : 'none')};

  & + & {
    border-top: 1px solid ${COLORS.hairline};
  }
`;

export const SegmentHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

export const SegmentHeading = styled.h3`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
`;

export const SegmentToggle = styled.button`
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.heading};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  /* The open segment's header: one segment is always open, so it has nothing to do. */
  &[aria-disabled='true'] {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

/* Points down at an open segment, to the side at a closed one. */
export const SegmentChevron = styled.span<{ $open: boolean }>`
  flex: none;
  display: inline-flex;
  color: ${COLORS.textMuted};
  transform: rotate(${({ $open }) => ($open ? 0 : -90)}deg);
  transition: transform ${ANIM.CREATIVE_CHEVRON_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* The segment's title, set as the panes' titles are. */
export const SegmentName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

/* A closed body is folded to nothing rather than taken out: its list keeps where it was scrolled to, and hidden
   it is out of reach of the pointer, the keyboard and assistive tech alike. */
export const SegmentBody = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-height: 0;

  ${({ $open }) =>
    $open
      ? css`
          flex: 1 1 auto;
          padding-bottom: ${SPACE[12]};
        `
      : css`
          flex: none;
          height: 0;
          overflow: hidden;
          visibility: hidden;
        `}
`;

/* A hand-picked list's results, beside the list. */
export const Results = styled.section`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  padding-right: ${SPACE[24]};
  border-right: 1px solid ${COLORS.hairline};

  ${MOBILE} {
    padding: 0 0 ${SPACE[16]};
    border-right: 0;
    border-bottom: 1px solid ${COLORS.hairline};
  }
`;

/* The list: an automatic list across the whole width, a hand-picked one beside its results, which take a third
   of the row to its two. */
export const Pane = styled.section`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};

  /* Alone (an automatic list, which has no results beside it), the whole row. */
  &:only-child {
    grid-column: 1 / -1;
  }
`;

export const PaneHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
  min-height: 36px;
`;

export const Spacer = styled.span`
  flex: 1 1 auto;
`;

/* A plain heading holding its Text, so the Games step's first one can take the keyboard (Back and the stepper
   leave it there). */
export const ListTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const Count = styled.span`
  flex: none;
  padding: 1px ${SPACE[8]}; /* 1px: a hairline of air around the digits inside the pill's border */
  border-radius: 999px;
  background: ${COLORS.ctaTint};
  border: 1px solid ${COLORS.ctaBorder};
  color: ${COLORS.text};
  font-size: 12px;
  font-weight: 700;
`;

/* Holds a hand-picked list's count and, as a game is added, the copy of it that grows and fades off it. */
export const CountWrap = styled.span`
  position: relative;
  flex: none;
  display: inline-flex;
`;

const countPing = keyframes`
  from {
    transform: scale(1);
    opacity: 0.9;
  }
  to {
    transform: scale(1.8);
    opacity: 0;
  }
`;

/** How long the count's copy plays; the component takes it away on the same beat. */
export const COUNT_PING_MS = ANIM.COUNT_PING_MS;

/* The count's copy, laid over it: it grows and fades once, then the component takes it away. Left out when
   motion is reduced, where it would only sit there. */
export const CountPing = styled(Count)`
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
  animation: ${countPing} ${ANIM.COUNT_PING_MS}ms ${ANIM.EASE} both;

  ${REDUCED_MOTION} {
    display: none;
  }
`;

export const SubLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/* `$flow`: an automatic list, which on a phone flows with the sheet rather than scrolling on its own. */
export const ListScroll = styled.div<{ $flow?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: ${SPACE[2]};

  ${MOBILE} {
    overflow-y: ${({ $flow }) => ($flow ? 'visible' : 'auto')};
  }
`;

/* Column COUNT follows the room there is, with a floor on the width: a fixed count left the cards too
   narrow for a long title once they carried a name strip, and the titles clipped. */
export const Thumbs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 128px), 1fr));
  gap: ${SPACE[12]};

  ${MOBILE} {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 120px), 1fr));
  }
`;

/* One game in the list, shaped like the curated banner cards in Website Creatives: the artwork with its
   controls laid on it, over a solid strip carrying the drag handle, the title and the maker. */
export const Slot = styled.div<{ $dragging: boolean }>`
  position: relative;
  min-width: 0;
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.35 : 1)};
  /* On a touch screen a game is lifted by resting a finger on it: no text selection and no iOS callout under that
     finger, while a swipe that starts on a game still scrolls until the hold completes. */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;

  &:active {
    cursor: grabbing;
  }
`;

export const Card = styled.div<{ $flash?: boolean }>`
  min-width: 0;
  border-radius: 14px;
  overflow: hidden;
  /* Solid, not translucent: the card is copied into the drag element, where a see-through one reads hollow. */
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  animation: ${({ $flash }) => ($flash ? addedFlash : 'none')} ${FLASH_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const Art = styled.div<{ $dim: boolean }>`
  position: relative;
  aspect-ratio: 1 / 1;
  background: ${COLORS.surfaceInset};
  opacity: ${({ $dim }) => ($dim ? 0.6 : 1)};
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

export const ArtImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* A game the catalogue no longer holds: in place of its art, what that means and what to do. */
export const Missing = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]};
  text-align: center;
  color: ${COLORS.textMuted};
`;

export const MissingText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  line-height: 1.35;
`;

/** The controls laid on the artwork, shaped like the banner card's remove button. */
export const SlotBtns = styled.div`
  position: absolute;
  top: ${SPACE[8]};
  right: ${SPACE[8]};
  z-index: 2;
  display: inline-flex;
  gap: ${SPACE[8]};
`;

export const SlotBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.heading};
  background: ${COLORS.surfaceCard};
  cursor: pointer;
  transition: filter ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    filter: brightness(1.35);
  }
`;

export const Tag = styled.span<{ $muted: boolean }>`
  position: absolute;
  left: ${SPACE[8]};
  bottom: ${SPACE[8]};
  z-index: 2;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  background: ${({ $muted }) => ($muted ? COLORS.surfaceCard : COLORS.ctaSolid)};
  color: ${({ $muted }) => ($muted ? COLORS.textMuted : COLORS.ctaSolidText)};
  border: 1px solid ${({ $muted }) => ($muted ? COLORS.hairline : COLORS.ctaBorder)};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]} ${SPACE[12]} ${SPACE[12]};
`;

export const DragHandle = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  padding-top: 1px; /* lines the grip up with the title's first line */
  color: ${COLORS.textFaint};
`;

/* Three lines are RESERVED for the title and the maker together, so cards in a row line up while a short
   title keeps its maker right under it instead of pushed to the bottom of a fixed two-line title. */
const LINE_PX = 15;
export const TitleCol = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: ${LINE_PX * 3}px;
`;

export const TileName = styled(Text).attrs({ tag: 'span' as const })<{ $dim: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12px;
  font-weight: 600;
  line-height: ${LINE_PX}px;
  color: ${({ $dim }) => ($dim ? COLORS.textMuted : COLORS.heading)};
`;

/** The maker's name, under the title, in both the list and the result rows. */
export const Provider = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  line-height: ${LINE_PX}px;
  color: ${COLORS.textFaint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The drag element: a solid copy of the card that follows the pointer, portalled to <body> so no
   ancestor's overflow clips it. */
export const DragLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  /* Over the sheet (2000), or the card being dragged disappears behind it. Same layer the tab's grid uses. */
  z-index: 3000;
  pointer-events: none;
`;

export const DragGhost = styled.div`
  border-radius: 14px;
  overflow: hidden;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  box-shadow: ${COLORS.menuShadow};
`;

/* ----------------------------------------------------------- empty states */

export const Guide = styled.div`
  padding: ${SPACE[40]} ${SPACE[32]};
  border: 1px dashed ${COLORS.hairline};
  border-radius: 14px;
  background: ${COLORS.surfaceFaint};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[12]};
  text-align: center;
  color: ${COLORS.textMuted};
`;

export const GuideTitle = styled(Text).attrs({ tag: 'h4' as const })`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const GuideText = styled(Text).attrs({ tag: 'span' as const })`
  margin: 0;
  max-width: 420px;
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

export const Note = styled(Text).attrs({ tag: 'span' as const })`
  margin: 0;
  padding: ${SPACE[20]} 0;
  text-align: center;
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

export const MoreRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACE[12]} 0 ${SPACE[2]};
`;

/* ----------------------------------------------------------- the results */

export const Search = styled.input`
  flex: none;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 ${SPACE[12]};
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  outline: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  /* The same focus as the hub's Field: its border in the accent, not the browser's outline. */
  &:focus {
    border-color: ${COLORS.accent};
  }

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const Rows = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding-right: ${SPACE[2]};
`;

export const Row = styled.button<{ $in: boolean }>`
  flex: none;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]};
  border-radius: 12px;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  background: ${({ $in }) => ($in ? COLORS.ctaTint : 'transparent')};
  border: 1px solid ${({ $in }) => ($in ? COLORS.ctaBorder : COLORS.hairline)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const RowArt = styled.span`
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RowText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

export const RowName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Pill = styled.span<{ $in: boolean }>`
  flex: none;
  height: 28px;
  padding: 0 ${SPACE[12]};
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  font-size: 12px;
  font-weight: 700;
  background: ${({ $in }) => ($in ? COLORS.ctaSolid : 'transparent')};
  color: ${({ $in }) => ($in ? COLORS.ctaSolidText : COLORS.text)};
  border: 1px solid ${({ $in }) => ($in ? COLORS.ctaBorder : COLORS.hairline)};
`;

/* ----------------------------------------------------------- the footer */

/* The confirm button reads as unavailable but is NOT `disabled`: a disabled button takes no click at
   all, and clicking it is exactly how someone asks "why can't I?". Its handler answers instead, by
   sending them to what is missing.
   It is also the way on from Method, whose label ("Continue with Hand-picked") is longer than what Cancel leaves it
   on a phone. There it wraps inside the pill: held to one line, it pushed the footer past the sheet's edge, where the
   sheet's clipping cut it off. The pill grows with its lines (three in Spanish and French at 320px), from the app's
   48px control height: held to that height, the first and the last line spilled out of it. */
export const PrimaryBtn = styled(Button)<{ $off: boolean }>`
  opacity: ${({ $off }) => ($off ? 0.45 : 1)};
  white-space: nowrap;

  ${MOBILE} {
    white-space: normal;
    height: auto;
    min-height: 48px;
  }
`;

/* On the left Back (adding) or Delete list (editing), on the right the actions. */
export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
  width: 100%;

  ${MOBILE} {
    flex-wrap: wrap;
    row-gap: ${SPACE[8]};
  }
`;

export const FooterNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/* The change summary. Left out on a phone, where the footer has room for its buttons only: Save's own look
   still says whether there is anything to save. */
export const Changes = styled(Text).attrs({ tag: 'span' as const })<{ $dirty: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $dirty }) => ($dirty ? COLORS.text : COLORS.textMuted)};

  ${MOBILE} {
    display: none;
  }
`;

/* Why Continue refused, beside it. A plain span rather than Text: it carries the live region's role, which Text
   does not pass on, and its copy arrives already translated. On the Games step it is always there and only its
   words change, so a refusal is read out. The step's hint (`waiting`) is drawn with it too, as a sibling WITHOUT the role:
   a refusal must stay the only thing that changes the region's words. */
export const FooterNotice = styled.span`
  min-width: 0;
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/* The collection a list from the market starts from, in the footer between Back and the way on: a tag, its name, where
   it sits and how many games it brings. Out of the way on a phone, where the footer has no room for it. */
export const Picked = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]} ${SPACE[16]} ${SPACE[8]} ${SPACE[8]};
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  font-size: 14px;

  ${MOBILE} {
    display: none;
  }
`;

export const PickedTag = styled(Text).attrs({ tag: 'span' as const })`
  flex: none;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 6px;
  background: ${COLORS.ctaTint};
  color: ${COLORS.ctaSolid};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const PickedName = styled.span`
  flex: none;
  font-weight: 700;
  color: ${COLORS.text};
`;

export const PickedMeta = styled.span`
  min-width: 0;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};

  ${MOBILE} {
    margin-left: auto;
  }
`;

export const DeleteBtn = styled.button`
  height: 34px;
  padding: 0 ${SPACE[4]};
  border: 0;
  background: transparent;
  color: ${STATUS_COLORS.negative};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  /* A delete already in flight: visibly held off, not a live control that silently does nothing. */
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/* ----------------------------------------------------------- the second look (the `list-wizard-v2` flag) */
/* From the Claude Design "Add list" prototype: the steps slide (here), and the automatic Games step is three filter
   cards (below). Its sizes and insets are the prototype's where they fall off the SPACE scale; each says so. */

export type StepDir = 'forward' | 'back';
/** `rest`: on show and still (the first step, or with motion reduced); `enter`: sliding in; `exit`: sliding out. */
export type StepPhase = 'rest' | 'enter' | 'exit';

const stepSlide = `${LIST_WIZARD_ANIM.STEP_SLIDE_MS}ms ${LIST_WIZARD_ANIM.STEP_EASE}`;
const stepFade = `${LIST_WIZARD_ANIM.STEP_FADE_MS}ms ${LIST_WIZARD_ANIM.EASE}`;
const slideBetween = (from: number, to: number): ReturnType<typeof keyframes> => keyframes`
  from {
    transform: translateX(${from}px);
  }
  to {
    transform: translateX(${to}px);
  }
`;
const OFFSET = LIST_WIZARD_ANIM.STEP_OFFSET_PX;
const slideInFromRight = slideBetween(OFFSET, 0);
const slideInFromLeft = slideBetween(-OFFSET, 0);
const slideOutToLeft = slideBetween(0, -OFFSET);
const slideOutToRight = slideBetween(0, OFFSET);
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

/* Where the steps slide: the one leaving laid over the one arriving while both move. Clipped across while they slide
   ($sliding), where the slide runs past the editor's sides, and only then: at rest a clip there cut what reaches past
   the step's edge, the open first and last cards' rings above all. `clip`, unlike `hidden`, makes no scroll container,
   so the phone's sticky filter bar still holds to the sheet. */
export const StepStage = styled.div<{ $sliding: boolean }>`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: ${({ $sliding }) => ($sliding ? 'clip' : 'visible')};

  ${MOBILE} {
    flex: none;
  }
`;

/* One step, laid out as the editor lays it (a column, its parts 16px apart). Arriving, it slides in from the side it
   lies on and shows a beat after it starts to move, and keeps nothing once it has (no transform left on it). Leaving,
   it is taken out of the flow, over the one arriving, and slides out the other way. Nothing moves with motion reduced,
   where the component leaves the step that leaves out altogether. */
export const StepLayer = styled.div<{ $phase: StepPhase; $dir: StepDir }>`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};

  ${({ $phase, $dir }) =>
    $phase === 'enter' &&
    css`
      animation:
        ${$dir === 'forward' ? slideInFromRight : slideInFromLeft} ${stepSlide},
        ${fadeIn} ${stepFade} ${LIST_WIZARD_ANIM.STEP_FADE_DELAY_MS}ms backwards;
    `}

  ${({ $phase, $dir }) =>
    $phase === 'exit' &&
    css`
      position: absolute;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
      animation:
        ${$dir === 'forward' ? slideOutToLeft : slideOutToRight} ${stepSlide} forwards,
        ${fadeOut} ${stepFade} forwards;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }

  ${MOBILE} {
    flex: none;
  }
`;

/* The automatic Games step's question: the Method step's size and weight, left-aligned over the cards, smaller on a
   phone. */
export const CardsQuestion = styled.h3`
  flex: none;
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${COLORS.heading};

  ${MOBILE} {
    font-size: 20px;
  }
`;

const cardMs = `${LIST_WIZARD_ANIM.CARD_MS}ms ${LIST_WIZARD_ANIM.EASE}`;
const cardColorMs = `${LIST_WIZARD_ANIM.CARD_COLOR_MS}ms ${LIST_WIZARD_ANIM.EASE}`;

/* The three cards, Provider, Type and Theme, side by side; stacked on a phone. Positioned (each card), because the
   open card's panel hangs from it over what is below. */
export const FilterCards = styled.div`
  flex: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${SPACE[12]};

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${SPACE[8]};
  }
`;

/* One filter's card. While nothing is picked it is roomy, with its description and button; once anything is, all three
   tighten to one line (18px of inset to 6px 10px, the prototype's), and a card holding a pick turns the brand orange.
   The open one is ringed. On a phone they are tight from the start. */
export const FilterCard = styled.div<{ $filled: boolean; $on: boolean; $open: boolean }>`
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ $filled }) => ($filled ? '6px 10px' : '18px')};
  border-radius: ${({ $filled }) => ($filled ? '12px' : '16px')};
  border: 1px solid ${({ $filled, $on }) => ($filled && $on ? COLORS.ctaSolid : LIST_WIZARD.line)};
  background: ${({ $filled, $on }) => ($filled && $on ? COLORS.ctaTint : LIST_WIZARD.card)};
  box-shadow: ${({ $open }) => ($open ? `0 0 0 2px ${COLORS.ctaSolid}` : 'none')};
  transition:
    padding ${cardMs},
    border-radius ${cardMs},
    background ${cardColorMs},
    border-color ${cardColorMs};

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    padding: 6px 10px;
    border-radius: 12px;
  }
`;

/* The card's header, which opens its panel: the icon, the filter's name and what it holds, and a chevron. */
export const FilterHead = styled.button`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: none;
  color: ${COLORS.text};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
    border-radius: 10px;
  }
`;

/* The filter's icon on its tile: 44px while roomy, 32px tight (36px on a phone); the brand's deep orange once the card
   holds a pick. The radii (12px, 9px, 10px) are the prototype's. */
export const FilterIcon = styled.span<{ $filled: boolean; $on: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $filled }) => ($filled ? SPACE[32] : SPACE[44])};
  height: ${({ $filled }) => ($filled ? SPACE[32] : SPACE[44])};
  border-radius: ${({ $filled }) => ($filled ? '9px' : '12px')};
  background: ${({ $on }) => ($on ? LIST_WIZARD.deep : LIST_WIZARD.tile)};
  color: ${({ $on }) => ($on ? LIST_WIZARD.soft : COLORS.textPale)};
  transition:
    width ${cardMs},
    height ${cardMs},
    border-radius ${cardMs},
    background ${cardColorMs},
    color ${cardColorMs};

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    width: ${SPACE[36]};
    height: ${SPACE[36]};
    border-radius: 10px;
  }
`;

export const FilterWords = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

/* The filter's name: large while the card is roomy, small and quiet over what it holds once tight. */
export const FilterTitle = styled(Text).attrs({ tag: 'span' as const })<{ $filled: boolean }>`
  font-size: ${({ $filled }) => ($filled ? '12px' : '19px')};
  font-weight: 700;
  color: ${({ $filled }) => ($filled ? LIST_WIZARD.muted : COLORS.textStrong)};
  transition:
    font-size ${cardMs},
    color ${cardColorMs};

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    font-size: ${({ $filled }) => ($filled ? '12px' : '16px')};
  }
`;

/* What the filter holds ("Any provider", "Pragmatic Play +2"): folded away while the cards are roomy, its line once
   they are tight. On a phone it always shows, quieter while nothing is picked. */
export const FilterValue = styled(Text).attrs({ tag: 'span' as const })<{ $filled: boolean }>`
  max-height: ${({ $filled }) => ($filled ? '22px' : '0')};
  opacity: ${({ $filled }) => ($filled ? 1 : 0)};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.text};
  transition:
    max-height ${cardMs},
    opacity ${cardColorMs};

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    max-height: 22px;
    opacity: 1;
    ${({ $filled }) =>
      $filled
        ? ''
        : css`
            font-size: 13px;
            font-weight: 500;
            color: ${LIST_WIZARD.muted};
          `}
  }
`;

export const FilterChevron = styled.span<{ $filled: boolean }>`
  flex: none;
  margin-left: auto;
  display: inline-flex;
  opacity: ${({ $filled }) => ($filled ? 1 : 0)};
  transition: opacity ${cardColorMs};

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    opacity: 1;
  }
`;

const foldMs = `${LIST_WIZARD_ANIM.CARD_FOLD_MS}ms ${LIST_WIZARD_ANIM.EASE}`;
const fadeMs = `${LIST_WIZARD_ANIM.CARD_FADE_MS}ms ${LIST_WIZARD_ANIM.EASE}`;

/* What the filter is, an example, and its button: shown while the card is roomy, folded away (and out of reach, once
   folded) when it tightens. 170px: room for the three, and the 10px between them, are the prototype's. Never on a
   phone. */
export const FilterMore = styled.div<{ $filled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: ${({ $filled }) => ($filled ? '0' : '10px')};
  max-height: ${({ $filled }) => ($filled ? '0' : '170px')};
  opacity: ${({ $filled }) => ($filled ? 0 : 1)};
  overflow: hidden;
  visibility: ${({ $filled }) => ($filled ? 'hidden' : 'visible')};
  transition:
    max-height ${foldMs},
    opacity ${fadeMs},
    margin-top ${cardMs},
    visibility 0s ${({ $filled }) => ($filled ? LIST_WIZARD_ANIM.CARD_FOLD_MS : 0)}ms;

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    display: none;
  }
`;

export const FilterWhat = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  line-height: 1.45;
  color: ${COLORS.textPale};
`;

export const FilterExample = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${LIST_WIZARD.muted};
`;

/* The card's own button, which opens its panel as the header does. 6px above it, the prototype's. */
export const FilterCta = styled.button`
  margin-top: 6px;
  height: 44px;
  border-radius: 99px;
  border: 1px solid ${LIST_WIZARD.dash};
  background: ${LIST_WIZARD.cta};
  color: ${COLORS.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${LIST_WIZARD.ctaHover};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }
`;

/* An open card's panel: hangs from the card's left edge, under it; the last card's from its right edge (CardMenuEnd), as
   the panel is wider than a card where the editor is narrow. On a phone either spans its card (Menu's own rule). */
export const CardMenu = styled(Menu)`
  left: 0;
`;

export const CardMenuEnd = styled(Menu)`
  left: auto;
  right: 0;

  ${MOBILE} {
    left: 0;
  }
`;

/* While nothing is picked, where the games will show: a dashed note filling the rest of the step. */
export const CardsNote = styled(Text).attrs({ tag: 'paragraph' as const })`
  flex: 1 1 auto;
  min-height: 56px;
  margin: 0;
  padding: 0 ${SPACE[20]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  border: 1px dashed ${LIST_WIZARD.dash};
  color: ${LIST_WIZARD.muted};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;

  ${MOBILE} {
    flex: none;
    min-height: 120px;
    padding: ${SPACE[16]};
  }
`;

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(${LIST_WIZARD_ANIM.RISE_PX}px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/* The matching games' heading, rising into place as a pick brings it ($rise), not whenever the step mounts. */
export const CardsHead = styled(PaneHead)<{ $rise: boolean }>`
  animation: ${({ $rise }) => ($rise ? rise : 'none')} ${LIST_WIZARD_ANIM.HEAD_RISE_MS}ms ${LIST_WIZARD_ANIM.EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* How many games match, in the brand's light orange (10px either side, the prototype's). */
export const MatchCount = styled.span`
  flex: none;
  height: 24px;
  padding: 0 10px;
  box-sizing: border-box;
  border-radius: 99px;
  border: 1px solid ${COLORS.ctaSolid};
  color: ${LIST_WIZARD.soft};
  display: inline-flex;
  align-items: center;
  font-size: 13px;
`;

/* Filters that match nothing together: a dashed note saying so. */
export const CardsNone = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  padding: ${SPACE[28]} ${SPACE[20]};
  border-radius: 14px;
  border: 1px dashed ${LIST_WIZARD.dash};
  color: ${COLORS.textPale};
  font-size: 15px;
  text-align: center;
`;

/* The matching games, each rising into place as a pick brings it ($rise; its card, not its slot, which a drag moves),
   not whenever the step mounts. */
export const RisingThumbs = styled(Thumbs)<{ $rise: boolean }>`
  & > [role='listitem'] > :first-child {
    animation: ${({ $rise }) => ($rise ? rise : 'none')} ${LIST_WIZARD_ANIM.TILE_RISE_MS}ms ${LIST_WIZARD_ANIM.EASE};
  }

  ${REDUCED_MOTION} {
    & > [role='listitem'] > :first-child {
      animation: none;
    }
  }
`;
